/**
 * @fileoverview ContextManager Component
 *
 * Admin-facing management UI for creating, editing, viewing, and deleting
 * reusable practice "contexts" (text, image, or audio) that can be attached to
 * questions. The component supports:
 * - Listing contexts with filtering, searching, pagination and lightweight previews
 * - Creating and updating contexts (including file upload for image/audio and transcript for audio)
 * - Viewing full context details in a dialog (text content, image preview, audio player, transcript)
 * - Safe deletion with usage checks (prevents removal when used by questions)
 * - Bulk deletion of selected contexts
 *
 * API Endpoints:
 * - GET /api/contexts/list - Lightweight list with previews/metadata
 * - GET /api/contexts/:id/full - Full payload for a single context
 * - POST /api/contexts - Create context
 * - PUT /api/contexts/:id - Update context
 * - GET /api/contexts/:id/usage - Usage summary
 * - DELETE /api/contexts/:id - Delete single
 * - DELETE /api/contexts/bulk - Bulk delete by ids
 *
 * Note: Lazy loading of binary content for performance optimization.
 *
 * @module ContextManager
 */
import { useState, useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  Plus,
  Search,
  FileText,
  FileImage,
  FileAudio,
  Eye,
  Edit,
  Trash2,
  Download,
  Filter,
  X,
  Save,
  Loader2,
} from "lucide-react";
import { getQuestionsByContextId, getQuestionById } from "@/lib/questionApi";
import QuestionPreview from "@/components/admin/QuestionPreview";
import { convertFileToBase64 } from "@/lib/fileUtils";
import {
  type Context,
  type ContextUsage,
  type ContextUsageSummary,
  getContextsList,
  getContextById,
  createContext as createContextApi,
  updateContext as updateContextApi,
  deleteContext as deleteContextApi,
  getContextUsage,
  getBulkContextUsage,
  bulkDeleteContexts,
  ConfirmationRequiredError,
  type SimilarContext,
  type ContextFormData as ApiContextFormData,
  fetchContextBinaryContent,
} from "@/lib/contextApi";
import { LazyBinaryContent } from "./answersManager/LazyBinaryContent";
import SimilarContextConfirmationDialog from "./SimilarContextConfirmationDialog";
import { logger } from '@/lib/logger';
import { CEFR_LEVELS, EXAM_TYPES, MODULES, SECTIONS } from '@/lib/constants';

// Context interface is imported from contextApi

/**
 * Interface for local form model for creating/updating a context
 *
 * @interface ContextFormData
 * @property {string} name - Context name
 * @property {'text' | 'image' | 'audio'} type - Context type
 * @property {string} content - Text content or file path
 * @property {string} [transcript] - Optional transcript for audio
 * @property {File} [file] - Selected file for binary types
 */
interface ContextFormData {
  name: string;
  type: "text" | "image" | "audio";
  content: string;
  transcript?: string;
  file?: File;
}

interface ContextFilterState {
  type: string;
  level: string;
  module: string;
  section: string;
  examType: string;
}

interface FilterSelectConfig {
  key: keyof ContextFilterState;
  placeholder: string;
  allLabel: string;
  triggerClassName: string;
  options: Array<{
    value: string;
    label: string;
  }>;
  showIcon?: boolean;
}

const DEFAULT_CONTEXT_FILTERS: ContextFilterState = {
  type: 'all',
  level: 'all',
  module: 'all',
  section: 'all',
  examType: 'all',
};

const CONTEXT_TYPE_OPTIONS = [
  { value: 'text', label: 'Text' },
  { value: 'image', label: 'Image' },
  { value: 'audio', label: 'Audio' },
];

const mergeFilterValues = <T extends string | number>(
  baseValues: readonly T[],
  discoveredValues: readonly T[],
): T[] => {
  const baseKeys = new Set(baseValues.map((value) => String(value)));
  const extraValues = Array.from(new Set(discoveredValues))
    .filter((value) => value !== null && value !== undefined && String(value))
    .filter((value) => !baseKeys.has(String(value)))
    .sort((a, b) =>
      String(a).localeCompare(String(b), undefined, { numeric: true }),
    );

  return [...baseValues, ...extraValues];
};

const getExamTypeLabel = (examType: string): string => {
  return (
    EXAM_TYPES.find((type) => type.value === examType)?.label ||
    examType.toUpperCase()
  );
};

const getModuleLabel = (module: string): string => {
  return (
    MODULES.find((item) => item.value === module)?.label ||
    module.charAt(0).toUpperCase() + module.slice(1)
  );
};

const contextMatchesFilterValue = (
  values: Array<string | number> | undefined,
  selectedValue: string,
): boolean => {
  if (selectedValue === 'all') {
    return true;
  }
  return (values || []).some((value) => String(value) === selectedValue);
};

const contextMatchesFilters = (
  context: Context,
  searchTerm: string,
  filters: ContextFilterState,
): boolean => {
  const normalizedSearch = searchTerm.trim().toLowerCase();
  const matchesSearch =
    !normalizedSearch ||
    [context.name, context.textPreview]
      .filter(Boolean)
      .some((value) => value!.toLowerCase().includes(normalizedSearch));

  return (
    matchesSearch &&
    contextMatchesFilterValue([context.type], filters.type) &&
    contextMatchesFilterValue(context.levels, filters.level) &&
    contextMatchesFilterValue(context.modules, filters.module) &&
    contextMatchesFilterValue(context.sections, filters.section) &&
    contextMatchesFilterValue(context.examTypes, filters.examType)
  );
};

/**
 * ContextManager Component
 *
 * Main component for managing exam contexts with CRUD operations,
 * file upload support, lazy loading, and usage validation.
 *
 * @returns {JSX.Element} Context management interface
 *
 * @component
 * @example
 * ```tsx
 * <ContextManager />
 * ```
 */
export default function ContextManager() {
  /**
   * State for contexts list fetched from API
   * Contains lightweight previews and metadata for performance
   *
   * @state {Context[]} contexts - Array of context objects
   */
  const queryClient = useQueryClient();

  const { data: contexts = [], isLoading: loading } = useQuery({
    queryKey: ['admin-contexts'],
    queryFn: getContextsList,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState<ContextFilterState>(
    DEFAULT_CONTEXT_FILTERS,
  );
  /**
   * IDs of contexts selected for bulk actions.
   */
  const [selectedContexts, setSelectedContexts] = useState<number[]>([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [editingContext, setEditingContext] = useState<Context | null>(null);
  const [viewingContext, setViewingContext] = useState<Context | null>(null);
  const { data: relatedQuestions = [], isLoading: loadingRelated } = useQuery({
    queryKey: ['admin-context-questions', viewingContext?.id],
    queryFn: () => getQuestionsByContextId(String(viewingContext?.id)),
    enabled: !!viewingContext?.id,
  });
  const [previewQuestion, setPreviewQuestion] = useState<any>(null);
  const [loadingQuestion, setLoadingQuestion] = useState(false);
  const [deletingContext, setDeletingContext] = useState<Context | null>(null);
  /**
   * Usage information returned by GET /api/contexts/:id/usage. If questionCount > 0, deletion is blocked.
   */
  const [contextUsage, setContextUsage] = useState<ContextUsage | null>(null);
  const [bulkUsageSummaries, setBulkUsageSummaries] = useState<
    ContextUsageSummary[]
  >([]);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  const [bulkDeleteSubmitting, setBulkDeleteSubmitting] = useState(false);
  const [formData, setFormData] = useState<ContextFormData>({
    name: "",
    type: "text",
    content: "",
    transcript: "",
  });
  const [uploading, setUploading] = useState(false);

  // Similar context confirmation dialog state
  const [showSimilarContextDialog, setShowSimilarContextDialog] =
    useState(false);
  const [similarContextData, setSimilarContextData] = useState<{
    similarContexts: SimilarContext[];
    maxSimilarity: number;
    threshold: number;
    message: string;
  } | null>(null);
  const [pendingContextData, setPendingContextData] = useState<{
    isUpdate: boolean;
    contextId?: number;
    data: ApiContextFormData;
  } | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const contextsPerPage = 10;
  const blockedContexts = bulkUsageSummaries.filter(
    (summary) => summary.questionCount > 0,
  );
  const deletableContexts = bulkUsageSummaries.filter(
    (summary) => summary.questionCount === 0,
  );
  const canBulkDelete =
    blockedContexts.length === 0 && bulkUsageSummaries.length > 0;

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [
    searchTerm,
    filters.type,
    filters.level,
    filters.module,
    filters.section,
    filters.examType,
  ]);

  /**
   * Fetch context with full text content and transcript (no binary content).
   * Used for text contexts (full text) and audio contexts (transcript).
   * Binary content (media files) is loaded by URL via LazyBinaryContent component.
   */
  const loadFullContext = async (contextId: number) => {
    try {
      return await getContextById(contextId);
    } catch (error: any) {
      logger.error("Error loading context details:", error);
      toast.error("Failed to load context details");
      return null;
    }
  };

  /**
   * Prepare context data from form state for API submission
   */
  const prepareContextData = async (
    formData: ContextFormData,
  ): Promise<ApiContextFormData> => {
    const contextData: ApiContextFormData = {
      name: formData.name,
      type: formData.type,
    };

    if (formData.type === "text") {
      contextData.content = formData.content;
    } else if (formData.file) {
      const base64 = await convertFileToBase64(formData.file);
      contextData.content = base64;
      contextData.fileName = formData.file.name;
      contextData.mimeType = formData.file.type;
    }

    if (formData.type === "audio" && formData.transcript) {
      contextData.transcript = formData.transcript;
    }

    return contextData;
  };

  /**
   * Handle confirmation required error by showing similarity dialog
   */
  const handleConfirmationRequired = (
    error: ConfirmationRequiredError,
    pendingData: ApiContextFormData,
    isUpdate: boolean,
    contextId?: number,
  ) => {
    setSimilarContextData({
      similarContexts: error.similarContexts,
      maxSimilarity: error.maxSimilarity,
      threshold: error.threshold,
      message: error.message,
    });
    setPendingContextData({
      isUpdate,
      contextId,
      data: pendingData,
    });
    setShowSimilarContextDialog(true);
  };

  /**
   * Handle context creation/update error with confirmation flow.
   */
  const handleContextError = async (
    error: any,
    formData: ContextFormData,
    isUpdate: boolean,
    contextId?: number,
  ) => {
    if (error instanceof ConfirmationRequiredError) {
      const pendingData = await prepareContextData(formData);
      if (isUpdate && formData.type === "audio") {
        pendingData.transcript = formData.transcript || "";
      }
      handleConfirmationRequired(error, pendingData, isUpdate, contextId);
    } else {
      const action = isUpdate ? "update" : "create";
      toast.error(
        `Failed to ${action} context: ${error.message || "Unknown error"}`,
      );
    }
  };

  /**
   * Validate form data before submission.
   */
  const validateFormData = (): boolean => {
    if (!formData.name || !formData.type) {
      return false;
    }
    if (!formData.content && !formData.file) {
      return false;
    }
    return true;
  };

  /**
   * Create a new context using the local form state.
   * - For text type, sends `content` text.
   * - For image/audio types, reads file as base64 and sends along with metadata.
   * - For audio type, optionally includes a shared `transcript`.
   */
  const handleCreateContext = async () => {
    if (!validateFormData()) {
      toast.error("Please fill in all required fields");
      return;
    }

    setUploading(true);
    try {
      const contextData = await prepareContextData(formData);
      await createContextApi(contextData);

      toast.success("Context created successfully!");
      setShowCreateDialog(false);
      resetForm();
      queryClient.invalidateQueries({ queryKey: ["admin-contexts"] });
    } catch (error: any) {
      logger.error("Error creating context:", error);
      await handleContextError(error, formData, false);
    } finally {
      setUploading(false);
    }
  };

  /**
   * Update the currently editing context with the local form state.
   * Heavy fields are only included if a new file is provided.
   */
  const handleUpdateContext = async () => {
    if (!editingContext || !validateFormData()) {
      toast.error("Please fill in all required fields");
      return;
    }

    setUploading(true);
    try {
      const contextData = await prepareContextData(formData);
      if (formData.type === "audio") {
        contextData.transcript = formData.transcript || "";
      }
      await updateContextApi(editingContext.id, contextData);

      toast.success("Context updated successfully!");
      setEditingContext(null);
      resetForm();
      queryClient.invalidateQueries({ queryKey: ["admin-contexts"] });
    } catch (error: any) {
      logger.error("Error updating context:", error);
      await handleContextError(error, formData, true, editingContext.id);
    } finally {
      setUploading(false);
    }
  };

  /**
   * Save context with confirmation (update or create).
   */
  const saveContextWithConfirmation = async (
    contextData: ApiContextFormData,
    isUpdate: boolean,
    contextId?: number,
  ) => {
    if (isUpdate && contextId) {
      await updateContextApi(contextId, contextData);
      toast.success("Context updated successfully!");
      setEditingContext(null);
    } else {
      await createContextApi(contextData);
      toast.success("Context created successfully!");
      setShowCreateDialog(false);
    }
    resetForm();
    queryClient.invalidateQueries({ queryKey: ["admin-contexts"] });
  };

  /**
   * Handle confirmation to save context despite similarity
   */
  const handleConfirmSave = async () => {
    if (!pendingContextData) {
      return;
    }

    setUploading(true);
    try {
      const contextDataWithConfirm = {
        ...pendingContextData.data,
        confirmSave: true,
      };

      await saveContextWithConfirmation(
        contextDataWithConfirm,
        pendingContextData.isUpdate,
        pendingContextData.contextId,
      );

      setShowSimilarContextDialog(false);
      setSimilarContextData(null);
      setPendingContextData(null);
    } catch (error: any) {
      logger.error("Error saving context:", error);
      toast.error(
        `Failed to save context: ${error.message || "Unknown error"}`,
      );
    } finally {
      setUploading(false);
    }
  };

  /**
   * Handle cancellation of save
   */
  const handleCancelSave = () => {
    setShowSimilarContextDialog(false);
    setSimilarContextData(null);
    setPendingContextData(null);
  };

  /**
   * Open the delete confirmation dialog and load usage info to determine if deletion is allowed.
   */
  const openDeleteDialog = async (context: Context) => {
    setDeletingContext(context);

    // Check if context is used by any questions
    try {
      const usage = await getContextUsage(context.id);
      setContextUsage(usage);
    } catch (error) {
      logger.error("Error checking context usage:", error);
      setContextUsage({ questionCount: 0, questions: [] });
    }

    setShowDeleteDialog(true);
  };

  /**
   * Permanently delete a single context. Requires that usage count is zero.
   */
  const handleDeleteContext = async () => {
    if (!deletingContext) return;

    try {
      await deleteContextApi(deletingContext.id);

      toast.success("Context deleted successfully!");
      setShowDeleteDialog(false);
      setDeletingContext(null);
      setContextUsage(null);
      queryClient.invalidateQueries({ queryKey: ["admin-contexts"] });
    } catch (error: any) {
      logger.error("Error deleting context:", error);
      toast.error(
        `Failed to delete context: ${error.message || "Unknown error"}`,
      );
    }
  };

  /**
   * Build ordered usage summaries matching selected context order
   */
  const buildOrderedUsageSummaries = (
    usageSummaries: ContextUsageSummary[],
    selectedIds: number[],
  ): ContextUsageSummary[] => {
    const usageMap = new Map(
      usageSummaries.map((summary) => [
        summary.contextId?.toString(),
        summary,
      ]),
    );

    return selectedIds.map((id) => {
      const key = id.toString();
      const existing = usageMap.get(key);
      if (existing) {
        return existing;
      }
      const contextRef = contexts.find((ctx) => ctx.id === id);
      return {
        contextId: id,
        contextName: contextRef?.name || null,
        questionCount: 0,
        questions: [],
      };
    });
  };

  /**
   * Load usage information for selected contexts and open the bulk delete dialog.
   */
  const openBulkDeleteDialog = async () => {
    if (selectedContexts.length === 0) {
      toast.error("Select at least one context to delete.");
      return;
    }

    setBulkDeleteLoading(true);
    try {
      const usageSummaries = await getBulkContextUsage(selectedContexts);
      const orderedSummaries = buildOrderedUsageSummaries(
        usageSummaries,
        selectedContexts,
      );
      setBulkUsageSummaries(orderedSummaries);
      setShowBulkDeleteDialog(true);
    } catch (error: any) {
      logger.error("Error loading usage for bulk delete:", error);
      toast.error(
        error.message || "Failed to load usage details for selected contexts.",
      );
    } finally {
      setBulkDeleteLoading(false);
    }
  };

  /**
   * Confirm bulk deletion when no selected contexts have question dependencies.
   */
  const handleBulkDeleteConfirm = async () => {
    if (selectedContexts.length === 0) {
      return;
    }

    setBulkDeleteSubmitting(true);
    try {
      await bulkDeleteContexts(selectedContexts);

      toast.success(
        `${selectedContexts.length} contexts deleted successfully!`,
      );
      setSelectedContexts([]);
      setShowBulkDeleteDialog(false);
      setBulkUsageSummaries([]);
      queryClient.invalidateQueries({ queryKey: ["admin-contexts"] });
    } catch (error: any) {
      logger.error("Error bulk deleting contexts:", error);
      toast.error(
        `Failed to delete contexts: ${error.message || "Unknown error"}`,
      );
    } finally {
      setBulkDeleteSubmitting(false);
    }
  };

  const handleBulkDeleteDialogChange = (open: boolean) => {
    setShowBulkDeleteDialog(open);
    if (!open) {
      setBulkUsageSummaries([]);
      setBulkDeleteSubmitting(false);
    }
  };

  /**
   * Reset the form state to its initial values.
   */
  const resetForm = () => {
    setFormData({
      name: "",
      type: "text",
      content: "",
      transcript: "",
    });
  };

  /**
   * Initialize form data from context for editing.
   */
  const initializeFormFromContext = (context: Context) => {
    setFormData({
      name: context.name,
      type: context.type,
      content: context.type === "text" ? context.content || "" : "",
      transcript: context.transcript || "",
    });
  };

  /**
   * Load full context and open the edit dialog with hydrated form values.
   */
  const openEditDialog = async (context: Context) => {
    const fullContext = await loadFullContext(context.id);
    if (fullContext) {
      setEditingContext(fullContext);
      initializeFormFromContext(fullContext);
    }
  };

  /**
   * Load additional context data based on type
   */
  const loadContextDataByType = async (
    context: Context,
  ): Promise<Partial<Context>> => {
    if (context.type !== "text" && context.type !== "audio") {
      return {};
    }

    const fullContext = await loadFullContext(context.id);
    if (!fullContext) {
      return {};
    }

    if (context.type === "text") {
      return { content: fullContext.content };
    }

    return { transcript: fullContext.transcript };
  };

  /**
   * Open the read-only view dialog with optimized lazy loading.
   * Uses lightweight context from list for all types.
   * For text/audio contexts, loads additional content (full text/transcript) on demand.
   * Media content (image/audio) is loaded by URL via LazyBinaryContent component.
   */
  const openViewDialog = async (context: Context) => {
    const additionalData = await loadContextDataByType(context);
    const viewingContextData: Context = {
      ...context,
      ...additionalData,
    };

    setViewingContext(viewingContextData);
    setShowViewDialog(true);
  };

  // Type configuration for icons and badge colors
  const typeConfig = {
    text: {
      icon: FileText,
      badgeColor: "bg-primary/10 text-primary border-primary/20",
    },
    image: {
      icon: FileImage,
      badgeColor: "bg-primary/10 text-primary border-primary/20",
    },
    audio: {
      icon: FileAudio,
      badgeColor: "bg-primary/10 text-primary border-primary/20",
    },
  } as const;

  /**
   * Return an icon component for a given context type.
   */
  const getTypeIcon = (type: string) => {
    const Icon = typeConfig[type as keyof typeof typeConfig]?.icon || FileText;
    return <Icon className="h-4 w-4" />;
  };

  /**
   * Return Tailwind class names used to color-code the type badge.
   */
  const getTypeBadgeColor = (type: string) => {
    return (
      typeConfig[type as keyof typeof typeConfig]?.badgeColor ||
      "bg-gray-500/10 text-gray-600 border-gray-200"
    );
  };

  /**
   * Get the description text for related questions section
   */
  const getRelatedQuestionsDescription = (): string => {
    if (loadingRelated) {
      return "Loading...";
    }
    if (relatedQuestions.length > 0) {
      const questionText = relatedQuestions.length === 1 ? "question" : "questions";
      return `${relatedQuestions.length} ${questionText} use this context`;
    }
    return "No questions use this context";
  };

  /**
   * Handle loading a question for preview.
   */
  const handleLoadQuestionPreview = async (questionId: number) => {
    try {
      setLoadingQuestion(true);
      const fullQuestion = await getQuestionById(String(questionId));
      setPreviewQuestion(fullQuestion);
    } catch (error) {
      toast.error("Failed to load question");
      logger.error("Error loading question:", error);
    } finally {
      setLoadingQuestion(false);
    }
  };

  /**
   * Render the related questions content based on loading and data state
   */
  const renderRelatedQuestionsContent = () => {
    if (loadingRelated) {
      return (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="h-4 w-4 animate-spin text-primary" />
        </div>
      );
    }

    if (relatedQuestions.length === 0) {
      return (
        <p className="font-ui text-xs text-muted-foreground text-center py-2">
          No questions use this context
        </p>
      );
    }

    return (
      <div className="space-y-2 max-h-64 overflow-y-auto">
        {relatedQuestions.map((question) => (
          <div
            key={question.id}
            className="p-2 bg-background rounded border border-border hover:border-primary/30 transition-colors"
          >
            <div className="flex items-start justify-between gap-2">
              <p className="font-ui text-xs text-foreground flex-1 line-clamp-2">
                {question.text}
              </p>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 flex-shrink-0 hover:bg-primary/10"
                onClick={() => handleLoadQuestionPreview(question.id)}
                title="View question preview"
                disabled={loadingQuestion}
              >
                <Eye className="h-4 w-4 text-primary" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  /**
   * Get paginated contexts from filtered list.
   */
  const getPaginatedContexts = (filtered: Context[]) => {
    const totalPages = Math.ceil(filtered.length / contextsPerPage);
    const startIndex = (currentPage - 1) * contextsPerPage;
    const endIndex = startIndex + contextsPerPage;
    const currentContexts = filtered.slice(startIndex, endIndex);
    return { totalPages, currentContexts };
  };

  const updateFilter = (key: keyof ContextFilterState, value: string) => {
    setFilters((currentFilters) => ({
      ...currentFilters,
      [key]: value,
    }));
  };

  const resetFilters = () => {
    setSearchTerm('');
    setFilters(DEFAULT_CONTEXT_FILTERS);
  };

  const levelOptions = useMemo(
    () =>
      mergeFilterValues(
        CEFR_LEVELS,
        contexts.flatMap((context) => context.levels || []),
      ),
    [contexts],
  );

  const sectionOptions = useMemo(
    () =>
      mergeFilterValues(
        SECTIONS,
        contexts.flatMap((context) => context.sections || []),
      ),
    [contexts],
  );

  const examTypeOptions = useMemo(
    () =>
      mergeFilterValues(
        EXAM_TYPES.map((type) => type.value),
        contexts.flatMap((context) => context.examTypes || []),
      ),
    [contexts],
  );

  const moduleOptions = useMemo(
    () =>
      mergeFilterValues(
        MODULES.map((module) => module.value),
        contexts.flatMap((context) => context.modules || []),
      ),
    [contexts],
  );

  const filterSelectConfigs: FilterSelectConfig[] = [
    {
      key: 'type',
      placeholder: 'Type',
      allLabel: 'All Types',
      triggerClassName: 'w-40',
      options: CONTEXT_TYPE_OPTIONS,
      showIcon: true,
    },
    {
      key: 'examType',
      placeholder: 'Exam Type',
      allLabel: 'All Exam Types',
      triggerClassName: 'w-48',
      options: examTypeOptions.map((examType) => ({
        value: examType,
        label: getExamTypeLabel(examType),
      })),
    },
    {
      key: 'level',
      placeholder: 'Level',
      allLabel: 'All Levels',
      triggerClassName: 'w-32',
      options: levelOptions.map((level) => ({ value: level, label: level })),
    },
    {
      key: 'module',
      placeholder: 'Module',
      allLabel: 'All Modules',
      triggerClassName: 'w-40',
      options: moduleOptions.map((module) => ({
        value: module,
        label: getModuleLabel(module),
      })),
    },
    {
      key: 'section',
      placeholder: 'Section',
      allLabel: 'All Sections',
      triggerClassName: 'w-36',
      options: sectionOptions.map((section) => ({
        value: String(section),
        label: `Section ${section}`,
      })),
    },
  ];

  const filteredContexts = useMemo(
    () =>
      contexts.filter((context) =>
        contextMatchesFilters(context, searchTerm, filters),
      ),
    [contexts, searchTerm, filters],
  );
  const { totalPages, currentContexts } = getPaginatedContexts(filteredContexts);
  const hasActiveFilters =
    searchTerm.trim() !== '' ||
    Object.values(filters).some((value) => value !== 'all');

  // Determine mode for form fields
  const formMode = editingContext ? "edit" : "create";

  // Helper: Render text content field
  const renderTextContentField = () => {
    return (
      <div>
        <Label
          htmlFor={formMode === 'create' ? 'content' : 'edit-content'}
          className="font-ui"
        >
          Text Content
        </Label>
        <Textarea
          id={formMode === "create" ? "content" : "edit-content"}
          value={formData.content}
          onChange={(e) =>
            setFormData({ ...formData, content: e.target.value })
          }
          placeholder="Enter the context text..."
          rows={formMode === "create" ? 10 : 5}
        />
      </div>
    );
  };

  // Helper: Render file upload field
  const renderFileUploadField = () => {
    const acceptType = formData.type === "image" ? "image/*" : "audio/*";
    const labelText = formMode === 'create'
      ? `Upload ${formData.type} File`
      : `Upload New ${formData.type} File (optional)`;

    return (
      <div>
        <Label htmlFor={formMode === 'create' ? 'file' : 'edit-file'} className="font-ui">
          {labelText}
        </Label>
        <Input
          id={formMode === "create" ? "file" : "edit-file"}
          type="file"
          accept={acceptType}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) {
              setFormData({ ...formData, file });
            }
          }}
        />
        {formMode === "edit" && (
          <p className="text-xs text-muted-foreground mt-1">
            Leave empty to keep the current file
          </p>
        )}
      </div>
    );
  };

  // Helper: Render audio transcript field
  const renderAudioTranscriptField = () => {
    return (
      <div>
        <Label
          htmlFor={
            formMode === 'create' ? 'transcript' : 'edit-transcript'
          }
          className="font-ui"
        >
          Audio Transcript (Optional)
        </Label>
        <Textarea
          id={
            formMode === "create" ? "transcript" : "edit-transcript"
          }
          value={formData.transcript || ""}
          onChange={(e) =>
            setFormData({ ...formData, transcript: e.target.value })
          }
          placeholder="Enter the audio transcript text..."
          rows={6}
        />
        <p className="font-ui text-xs text-muted-foreground mt-1">
          Provide a written transcript of the audio content. This
          will be shared across all questions using this audio
          context.
        </p>
      </div>
    );
  };

  // Helper: Render form action buttons
  const renderFormActionButtons = () => {
    const handleCancel = () => {
      setShowCreateDialog(false);
      setEditingContext(null);
      resetForm();
    };

    const getButtonContent = () => {
      if (uploading) {
        return (
          <>
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
            <span className="font-ui">
              {editingContext ? 'Updating...' : 'Creating...'}
            </span>
          </>
        );
      }
      return (
        <>
          <Save className="h-4 w-4 mr-2" />
          <span className="font-ui">
            {editingContext ? 'Update Context' : 'Create Context'}
          </span>
        </>
      );
    };

    return (
      <div className="flex justify-end gap-2 mt-6">
        <Button variant="outline" onClick={handleCancel}>
          <span className="font-ui">Cancel</span>
        </Button>
        <Button
          onClick={
            editingContext ? handleUpdateContext : handleCreateContext
          }
          disabled={uploading}
        >
          {getButtonContent()}
        </Button>
      </div>
    );
  };

  // Helper: Render form dialog content
  const renderFormDialogContent = () => {
    const renderContentFields = () => {
      if (formData.type === "text") {
        return renderTextContentField();
      }
      return (
        <>
          {renderFileUploadField()}
          {formData.type === "audio" && renderAudioTranscriptField()}
        </>
      );
    };

    return (
      <div className="space-y-4">
        <div>
          <Label htmlFor={formMode === 'create' ? 'name' : 'edit-name'} className="font-ui">
            Context Name
          </Label>
          <Input
            id={formMode === "create" ? "name" : "edit-name"}
            value={formData.name}
            onChange={(e) =>
              setFormData({ ...formData, name: e.target.value })
            }
            placeholder="e.g., German Family Introduction"
          />
        </div>
        <div>
          <Label htmlFor={formMode === 'create' ? 'type' : 'edit-type'} className="font-ui">
            Context Type
          </Label>
          <Select
            value={formData.type}
            onValueChange={(value: "text" | "image" | "audio") =>
              setFormData({ ...formData, type: value })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="text">Text</SelectItem>
              <SelectItem value="image">Image</SelectItem>
              <SelectItem value="audio">Audio</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {renderContentFields()}
        {renderFormActionButtons()}
      </div>
    );
  };

  // Helper: Render view dialog content
  const renderViewDialogContent = () => {
    if (!viewingContext) {
      return null;
    }

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="font-ui text-sm font-medium">Type</Label>
            <Badge
              className={`font-ui mt-1 ${getTypeBadgeColor(viewingContext.type)}`}
            >
              {viewingContext.type.toUpperCase()}
            </Badge>
          </div>
          <div>
            <Label className="font-ui text-sm font-medium">Created</Label>
            <p className="font-ui text-sm text-muted-foreground mt-1">
              {new Date(viewingContext.createdAt).toLocaleString()}
            </p>
          </div>
        </div>

        <div>
          <Label className="font-ui text-sm font-medium">Content Preview</Label>
          <div className="mt-2 border rounded-lg p-4">
            {viewingContext.type === 'text' && (
              <div className="prose max-w-none">
                <p className="font-ui whitespace-pre-wrap">
                  {viewingContext.content}
                </p>
              </div>
            )}
            {(viewingContext.type === 'image' || viewingContext.type === 'audio') && (
              <div>
                {viewingContext.id !== undefined && (
                  <LazyBinaryContent
                    contextId={viewingContext.id}
                    mimeType={viewingContext.mimeType}
                    contextTitle={viewingContext.name}
                    contextType={viewingContext.type}
                    mediaUrl={viewingContext.mediaUrl}
                    initialBase64={viewingContext.binaryContent}
                    fetchBinaryContent={fetchContextBinaryContent}
                  />
                )}

                {viewingContext.type === 'audio' && viewingContext.transcript && (
                  <div className="mt-4 border-t pt-4">
                    <div className="flex items-center gap-2 mb-2">
                      <FileText className="h-4 w-4" />
                      <span className="font-ui font-medium text-sm">
                        Transcript:
                      </span>
                    </div>
                    <div className="bg-muted p-3 rounded-md">
                      <p className="font-ui text-sm whitespace-pre-wrap">
                        {viewingContext.transcript}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <Card className="border-primary/20 bg-primary/5">
          <CardHeader className="pb-2">
            <CardTitle className="font-ui text-sm font-semibold flex items-center gap-1.5 text-foreground">
              <FileText className="h-4 w-4" />
              Questions Using This Context
            </CardTitle>
            <CardDescription className="font-ui text-xs text-muted-foreground">
              {getRelatedQuestionsDescription()}
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            {renderRelatedQuestionsContent()}
          </CardContent>
        </Card>
      </div>
    );
  };

  // Helper: Render delete dialog content
  const renderDeleteDialogContent = () => {
    if (!deletingContext) {
      return null;
    }

    return (
      <div className="space-y-4">
        <div className="p-4 bg-muted rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            {getTypeIcon(deletingContext.type)}
            <span className="font-ui font-medium">{deletingContext.name}</span>
            <Badge
              className={`font-ui text-xs ${getTypeBadgeColor(deletingContext.type)}`}
            >
              {deletingContext.type.toUpperCase()}
            </Badge>
          </div>
          <p className="font-ui text-sm text-muted-foreground">
            Created:{' '}
            {new Date(deletingContext.createdAt).toLocaleDateString()}
          </p>
        </div>

        {contextUsage?.questionCount > 0 ? (
          <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <X className="h-4 w-4 text-destructive" />
              <span className="font-ui font-medium text-destructive">
                Cannot Delete
              </span>
            </div>
            <p className="font-ui text-sm text-destructive mb-3">
              This context is currently being used by{' '}
              <strong>{contextUsage.questionCount}</strong> question(s)
              and cannot be deleted.
            </p>

            {contextUsage.questions?.length > 0 && (
              <div>
                <p className="font-ui text-sm font-medium mb-2">
                  Used in the following questions:
                </p>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {contextUsage.questions.map((question: any) => (
                    <div
                      key={question.id}
                      className="font-ui text-xs bg-background p-2 rounded border"
                    >
                      <span className="font-medium">
                        Q{question.id}:
                      </span>{' '}
                      {question.question_text.substring(0, 80)}...
                    </div>
                  ))}
                </div>
              </div>
            )}

            <p className="font-ui text-xs text-muted-foreground mt-2">
              To delete this context, first remove it from all questions
              or delete those questions.
            </p>
          </div>
        ) : (
          <div className="p-4 bg-muted border border-border rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <X className="h-4 w-4 text-muted-foreground" />
              <span className="font-ui font-medium text-foreground">
                Confirm Deletion
              </span>
            </div>
            <p className="font-ui text-sm text-muted-foreground">
              This context is not currently used by any questions and can
              be safely deleted.
            </p>
          </div>
        )}
      </div>
    );
  };

  // Helper: Render bulk delete dialog content
  const renderBulkDeleteDialogContent = () => {
    if (bulkUsageSummaries.length === 0) {
      return (
        <div className="font-ui py-6 text-center text-sm text-muted-foreground">
          Select contexts to see usage details before deletion.
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {blockedContexts.length > 0 && (
          <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <X className="h-4 w-4 text-destructive" />
              <span className="font-ui font-medium text-destructive">
                {blockedContexts.length} context(s) cannot be deleted yet
              </span>
            </div>
            <p className="font-ui text-sm text-destructive">
              Remove these contexts from their connected questions before
              retrying bulk deletion.
            </p>
          </div>
        )}

        <div className="max-h-72 overflow-y-auto space-y-3">
          {bulkUsageSummaries.map((summary) => (
            <div
              key={summary.contextId}
              className={`rounded-lg border p-3 ${summary.questionCount > 0 ? "border-destructive/40 bg-destructive/5" : "border-muted bg-card"}`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-ui font-medium">
                    {summary.contextName ||
                      `Context ${summary.contextId}`}
                  </p>
                  <p className="font-ui text-xs text-muted-foreground">
                    ID: {summary.contextId}
                  </p>
                </div>
                <Badge
                  variant={
                    summary.questionCount > 0
                      ? 'destructive'
                      : 'secondary'
                  }
                >
                  <span className="font-ui">
                    {summary.questionCount > 0
                      ? `${summary.questionCount} linked question(s)`
                      : 'No linked questions'}
                  </span>
                </Badge>
              </div>
              {summary.questionCount > 0 &&
                summary.questions.length > 0 && (
                  <div className="mt-2 space-y-1">
                    <p className="font-ui text-xs font-medium text-muted-foreground">
                      Used in the following questions:
                    </p>
                    <div className="max-h-24 overflow-y-auto space-y-1">
                      {summary.questions.slice(0, 3).map((question) => {
                        const questionText = question.question_text || '';
                        return (
                          <div
                            key={question.id}
                            className="font-ui text-xs bg-background border rounded p-2"
                          >
                            <span className="font-medium mr-1">
                              Q{question.id}:
                            </span>
                            {questionText.substring(0, 80)}
                            {questionText.length > 80 ? '...' : ''}
                          </div>
                        );
                      })}
                      {summary.questions.length > 3 && (
                        <p className="font-ui text-xs italic text-muted-foreground">
                          +{summary.questions.length - 3} more question(s)
                        </p>
                      )}
                    </div>
                  </div>
                )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Helper: Add context to selection
  const addContextToSelection = (contextId: number) => {
    setSelectedContexts([
      ...selectedContexts,
      contextId,
    ]);
  };

  // Helper: Remove context from selection
  const removeContextFromSelection = (contextId: number) => {
    setSelectedContexts(
      selectedContexts.filter(
        (id) => id !== contextId,
      ),
    );
  };

  // Helper: Handle context selection checkbox change
  const handleContextSelectionChange = (contextId: number, checked: boolean | string | undefined) => {
    if (checked === true) {
      addContextToSelection(contextId);
    } else {
      removeContextFromSelection(contextId);
    }
  };

  // Helper: Render table body content
  const renderTableBody = () => {
    if (loading) {
      return (
        <TableRow>
          <TableCell colSpan={8} className="text-center py-8">
            <div className="flex items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
              <span className="font-ui ml-2">Loading contexts...</span>
            </div>
          </TableCell>
        </TableRow>
      );
    }

    if (filteredContexts.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={8} className="text-center py-8">
            <div className="flex flex-col items-center gap-2 text-muted-foreground">
              <FileText className="h-8 w-8" />
              <p className="font-ui text-lg font-medium">
                No contexts found
              </p>
              <p className="font-ui text-sm">
                Create your first context to get started.
              </p>
            </div>
          </TableCell>
        </TableRow>
      );
    }

    return (
      <>
        {currentContexts.map((context) => {
          return (
            <TableRow key={context.id}>
              <TableCell>
                <Checkbox
                  checked={selectedContexts.includes(context.id)}
                  onCheckedChange={(checked) => handleContextSelectionChange(context.id, checked)}
                />
              </TableCell>
              <TableCell className="w-64">
                <div className="flex items-center gap-2">
                  {getTypeIcon(context.type)}
                  <span className="font-ui font-medium truncate">
                    {context.name}
                  </span>
                </div>
              </TableCell>
              <TableCell className="w-24">
                <Badge
                  className={`font-ui text-xs whitespace-nowrap ${getTypeBadgeColor(context.type)}`}
                >
                  {context.type.toUpperCase()}
                </Badge>
              </TableCell>
              <TableCell className="w-32">
                {context.generationModel ? (
                  <Badge variant="outline" className="font-ui text-xs whitespace-nowrap">
                    {context.generationModel}
                  </Badge>
                ) : (
                  <span className="font-ui text-xs text-muted-foreground">—</span>
                )}
              </TableCell>
              <TableCell className="w-96 max-w-96">
                <div className="min-w-0">
                  {context.type === 'text' && (
                    <p className="font-ui text-sm text-muted-foreground truncate">
                      {context.textPreview || 'No content'}
                    </p>
                  )}
                  {context.type !== 'text' && (
                    <div className="font-ui flex items-center gap-2 text-sm text-muted-foreground">
                      <span className="capitalize">
                        {context.type}
                      </span>
                      <span>file</span>
                      {context.binarySize !== undefined && context.binarySize !== null && (
                        <span className="text-xs">
                          ({(context.binarySize / 1024).toFixed(1)}{' '}
                          KB)
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </TableCell>
              <TableCell className="w-32 font-ui text-xs text-muted-foreground">
                {new Date(context.createdAt).toLocaleDateString()}
              </TableCell>
              <TableCell className="w-32 font-ui text-xs text-muted-foreground">
                {new Date(context.updatedAt).toLocaleDateString()}
              </TableCell>
              <TableCell className="w-32">
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => openViewDialog(context)}
                    className="h-8 w-8"
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => openEditDialog(context)}
                    className="h-8 w-8"
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => openDeleteDialog(context)}
                    className="h-8 w-8 text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          );
        })}
      </>
    );
  };

  return (
    <div className="space-y-6">
      {/* Context Form Modal */}
      <Dialog
        open={!!(showCreateDialog || editingContext)}
        onOpenChange={(open) => {
          if (!open) {
            setShowCreateDialog(false);
            setEditingContext(null);
            resetForm();
          }
        }}
      >
        <DialogContent
          className="max-w-4xl max-h-[90vh] overflow-y-auto"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="font-ui">
              {editingContext ? 'Edit Context' : 'Create New Context'}
            </DialogTitle>
            <DialogDescription className="font-ui">
              {editingContext
                ? 'Modify the context details below.'
                : 'Fill in the details to create a new context.'}
            </DialogDescription>
          </DialogHeader>
          {renderFormDialogContent()}
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Dialog */}
      <Dialog
        open={showBulkDeleteDialog}
        onOpenChange={handleBulkDeleteDialogChange}
      >
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="font-ui flex items-center gap-2 text-destructive">
              <Trash2 className="h-5 w-5" />
              Delete Selected Contexts
            </DialogTitle>
            <DialogDescription className="font-ui">
              Review usage information for the selected contexts. Contexts that
              are still referenced by questions must be detached before
              deletion.
            </DialogDescription>
          </DialogHeader>
          {renderBulkDeleteDialogContent()}

          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => handleBulkDeleteDialogChange(false)}
            >
              <span className="font-ui">Cancel</span>
            </Button>
            <Button
              variant="destructive"
              disabled={!canBulkDelete || bulkDeleteSubmitting}
              onClick={handleBulkDeleteConfirm}
              className="flex items-center gap-2"
            >
              <Trash2 className="h-4 w-4" />
              <span className="font-ui">
                Delete {deletableContexts.length} context(s)
              </span>
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Context Management Panel */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="font-ui text-xl font-bold flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Context Management
              </CardTitle>
              <CardDescription className="font-ui">
                Manage reusable contexts (text, image, audio) for exam questions
              </CardDescription>
            </div>
            <Button
              onClick={() => setShowCreateDialog(true)}
              className="flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              <span className="font-ui">Add Context</span>
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Search and Filter Bar */}
          <div className="flex flex-col gap-3 mb-6 xl:flex-row xl:items-center">
            <div className="relative min-w-[240px] flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Search contexts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex flex-wrap gap-3">
              {filterSelectConfigs.map((config) => (
                <Select
                  key={config.key}
                  value={filters[config.key]}
                  onValueChange={(value) => updateFilter(config.key, value)}
                >
                  <SelectTrigger className={config.triggerClassName}>
                    {config.showIcon && <Filter className="h-4 w-4 mr-2" />}
                    <SelectValue placeholder={config.placeholder} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      <span className="font-ui">{config.allLabel}</span>
                    </SelectItem>
                    {config.options.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        <span className="font-ui">{option.label}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ))}
              {hasActiveFilters && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={resetFilters}
                  className="h-10 shrink-0"
                >
                  <X className="h-4 w-4 mr-2" />
                  <span className="font-ui">Reset</span>
                </Button>
              )}
            </div>
          </div>

          {/* Bulk Actions */}
          {selectedContexts.length > 0 && (
            <div className="flex items-center gap-2 mb-4 p-3 bg-muted rounded-lg">
              <span className="font-ui text-sm text-muted-foreground">
                {selectedContexts.length} context(s) selected
              </span>
              <Button variant="outline" size="sm">
                <Download className="h-4 w-4 mr-2" />
                <span className="font-ui">Export Selected</span>
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={openBulkDeleteDialog}
                disabled={bulkDeleteLoading}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                <span className="font-ui">Delete Selected</span>
              </Button>
            </div>
          )}

          {/* Contexts Table */}
          <div className="border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <Table className="table-fixed min-w-full">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox
                        checked={
                          selectedContexts.length === currentContexts.length &&
                          currentContexts.length > 0
                        }
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedContexts(
                              currentContexts.map((c) => c.id),
                            );
                          } else {
                            setSelectedContexts([]);
                          }
                        }}
                      />
                    </TableHead>
                    <TableHead className="w-64 font-ui">Name</TableHead>
                    <TableHead className="w-24 font-ui">Type</TableHead>
                    <TableHead className="w-32 font-ui">Model</TableHead>
                    <TableHead className="w-96 font-ui">Content Preview</TableHead>
                    <TableHead className="w-32 font-ui">Created</TableHead>
                    <TableHead className="w-32 font-ui">Modified</TableHead>
                    <TableHead className="w-32 font-ui">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {renderTableBody()}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Pagination Controls */}
          {filteredContexts.length > 0 && (
            <div className="flex items-center justify-between px-4 py-4 border-t">
              <p className="font-ui text-sm text-muted-foreground">
                Showing {currentContexts.length} of {filteredContexts.length}{' '}
                contexts (Page {currentPage} of {totalPages})
              </p>
              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage <= 1 || loading}
                  >
                    <span className="font-ui">Previous</span>
                  </Button>
                  <span className="font-ui text-sm text-muted-foreground px-2">
                    {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage >= totalPages || loading}
                  >
                    <span className="font-ui">Next</span>
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Stats */}
          <div className="font-ui flex items-center justify-between mt-4 text-sm text-muted-foreground">
            <span>
              Showing {filteredContexts.length} of {contexts.length} contexts
            </span>
            <div className="flex gap-4">
              <span>
                Text: {contexts.filter((c) => c.type === 'text').length}
              </span>
              <span>
                Image: {contexts.filter((c) => c.type === 'image').length}
              </span>
              <span>
                Audio: {contexts.filter((c) => c.type === 'audio').length}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* View Context Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="font-ui flex items-center gap-2">
              {viewingContext && getTypeIcon(viewingContext.type)}
              {viewingContext?.name}
            </DialogTitle>
            <DialogDescription className="font-ui">Context details and preview</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto">
            {renderViewDialogContent()}
          </div>
          <div className="flex-shrink-0 flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowViewDialog(false)}>
              <span className="font-ui">Close</span>
            </Button>
            {viewingContext && (
              <Button
                onClick={() => {
                  setShowViewDialog(false);
                  openEditDialog(viewingContext);
                }}
              >
                <Edit className="h-4 w-4 mr-2" />
                <span className="font-ui">Edit</span>
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-ui flex items-center gap-2 text-destructive">
              <Trash2 className="h-5 w-5" />
              Delete Context
            </DialogTitle>
            <DialogDescription className="font-ui">
              This action cannot be undone. Please review the details below
              before proceeding.
            </DialogDescription>
          </DialogHeader>

          {renderDeleteDialogContent()}

          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
            >
              Cancel
            </Button>
            {contextUsage?.questionCount === 0 && (
              <Button
                variant="destructive"
                onClick={handleDeleteContext}
                className="flex items-center gap-2"
              >
                <Trash2 className="h-4 w-4" />
                Delete Context
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Question Preview Dialog */}
      <Dialog
        open={!!previewQuestion}
        onOpenChange={(open) => !open && setPreviewQuestion(null)}
      >
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-ui">Question Preview</DialogTitle>
            <DialogDescription className="font-ui">
              Preview how this question will appear to students.
            </DialogDescription>
          </DialogHeader>
          {previewQuestion && (
            <QuestionPreview
              question={previewQuestion}
              onClose={() => setPreviewQuestion(null)}
              onViewRelated={async (relatedQuestion: any) => {
                // Close current preview and open preview of related question
                try {
                  setLoadingQuestion(true);
                  const fullQuestion = await getQuestionById(
                    relatedQuestion.id,
                  );
                  setPreviewQuestion(fullQuestion);
                } catch (error) {
                  toast.error("Failed to load related question");
                  logger.error("Error loading related question:", error);
                } finally {
                  setLoadingQuestion(false);
                }
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Similar Context Confirmation Dialog */}
      {similarContextData && (
        <SimilarContextConfirmationDialog
          open={showSimilarContextDialog}
          onOpenChange={setShowSimilarContextDialog}
          similarContexts={similarContextData.similarContexts}
          maxSimilarity={similarContextData.maxSimilarity}
          threshold={similarContextData.threshold}
          message={similarContextData.message}
          onConfirm={handleConfirmSave}
          onCancel={handleCancelSave}
        />
      )}
    </div>
  );
}
